/**
 * useProjectDefaultModel — project-level default AI model setting.
 *
 * Reads `projects/{pid}.defaultAiModel` (loose field, not yet in
 * canonical Project schema — added inline like other recent grid fields,
 * promotion to schema-models comes with the next bump cycle).
 *
 * Falls back to the frontend mirror of `CONTENT_PLAN_DEFAULT_MODEL` when the
 * project doc has no default set. Consumers (PerCellGenerateDialog, future model
 * selectors in content-plan) read `value` and pass it as the initial
 * ModelSelector value; calling `setDefault(model)` persists it project-wide
 * via a single updateDoc patch.
 */

import { useEffect, useState } from 'react';
import {
  doc,
  onSnapshot,
  serverTimestamp,
  updateDoc,
} from 'firebase/firestore';
import { CoreFirebaseService } from '@modules/firebase-core/services/CoreFirebaseService';
import { normalizeModelId } from '@modules/ui-components';
import { CONTENT_PLAN_DEFAULT_MODEL } from '../constants/modelDefaults';

/**
 * Fallback when the project doc has no `defaultAiModel` field yet.
 * Mirrors the backend's `CONTENT_PLAN_DEFAULT_MODEL`.
 */
export const PROJECT_FALLBACK_DEFAULT_MODEL = CONTENT_PLAN_DEFAULT_MODEL;

export interface UseProjectDefaultModelReturn {
  /** Current default model id (from project doc) or fallback. */
  value: string;
  /** True until the first onSnapshot resolves. */
  loading: boolean;
  /** Persist the chosen model as the project's default. */
  setDefault: (model: string) => Promise<void>;
}

export function useProjectDefaultModel(
  projectId: string | null | undefined,
): UseProjectDefaultModelReturn {
  const [value, setValue] = useState<string>(PROJECT_FALLBACK_DEFAULT_MODEL);
  const [loading, setLoading] = useState<boolean>(Boolean(projectId));

  useEffect(() => {
    if (!projectId) {
      setValue(PROJECT_FALLBACK_DEFAULT_MODEL);
      setLoading(false);
      return;
    }
    setLoading(true);
    const db = CoreFirebaseService.getInstance().firestore;
    const ref = doc(db, 'projects', projectId);
    const unsub = onSnapshot(ref, (snap) => {
      const data = snap.data() as { defaultAiModel?: string } | undefined;
      setValue(normalizeModelId(data?.defaultAiModel || PROJECT_FALLBACK_DEFAULT_MODEL));
      setLoading(false);
    }, () => {
      // On error fall back to hardcoded; the consumer will still work.
      setLoading(false);
    });
    return () => unsub();
  }, [projectId]);

  const setDefault = async (model: string): Promise<void> => {
    if (!projectId) return;
    const db = CoreFirebaseService.getInstance().firestore;
    const ref = doc(db, 'projects', projectId);
    await updateDoc(ref, {
      defaultAiModel: normalizeModelId(model),
      updatedAt: serverTimestamp(),
    });
  };

  return { value, loading, setDefault };
}
